import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './OpenSourcePage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import GithubIcon from '../components/shared/GithubIcon';
import { isContentfulConfigured } from '../contentful/client';
import { fetchOpenSourceLibraries } from '../contentful/openSourceLibrariesApi';
import openSourceProjectsData from '../data/openSourceProjects.json';
import githubDownloadsData from '../data/githubDownloads.json';
import codecovRepoOverrides from '../data/codecovRepoOverrides.json';

const CODECOV_DEFAULT_BRANCH = 'master';

const PYPI_PACKAGE_BY_REPO = new Map(
  (githubDownloadsData.byRepo || []).map((row) => [row.repo, row.pypiPackage || ''])
);

function getPypiPackageName(repo) {
  if (!repo || typeof repo !== 'string') return '';
  const fromIndex = PYPI_PACKAGE_BY_REPO.get(repo);
  if (fromIndex && String(fromIndex).trim()) return String(fromIndex).trim().toLowerCase();
  const name = repo.split('/')[1];
  if (!name) return '';
  return name.replace(/-/g, '').replace(/\./g, '').toLowerCase();
}

function getCodecovRepoSlug(githubRepo) {
  if (!githubRepo || typeof githubRepo !== 'string') return githubRepo;
  const o = codecovRepoOverrides[githubRepo];
  if (typeof o === 'string' && o.trim()) return o.trim();
  return githubRepo;
}

function buildExternalBadgeUrls(repo) {
  if (!repo || typeof repo !== 'string') return null;
  const parts = repo.split('/');
  if (parts.length !== 2) return null;
  const pypi = getPypiPackageName(repo);
  const enc = (s) => encodeURIComponent(s);
  const covSlug = getCodecovRepoSlug(repo);
  const covParts = covSlug.split('/');
  const covOk = covParts.length === 2;
  const [co, cr] = covOk ? covParts : [null, null];
  return {
    pypiPackage: pypi,
    pepy: pypi ? `https://static.pepy.tech/badge/${enc(pypi)}` : null,
    pypiVersionSvg: pypi ? `https://img.shields.io/pypi/v/${enc(pypi)}.svg` : null,
    codecovImg: covOk
      ? `https://codecov.io/gh/${enc(co)}/${enc(cr)}/branch/${encodeURIComponent(CODECOV_DEFAULT_BRANCH)}/graph/badge.svg`
      : null,
    codecovHref: covOk ? `https://app.codecov.io/gh/${enc(co)}/${enc(cr)}` : null,
  };
}

function shieldMessageIndicatesInvalid(message) {
  if (message == null) return true;
  const s = String(message).trim().toLowerCase();
  if (s === '') return true;
  if (s === 'unknown') return true;
  if (s === 'invalid') return true;
  if (s.includes('not found')) return true;
  if (s.includes('no data')) return true;
  return false;
}

async function fetchShieldOk(jsonUrl) {
  try {
    const res = await fetch(jsonUrl);
    if (!res.ok) return false;
    const data = await res.json();
    const msg = data.message ?? data.value ?? '';
    return !shieldMessageIndicatesInvalid(msg);
  } catch {
    return false;
  }
}

const ExternalProjectBadges = ({ repo, title }) => {
  const [visible, setVisible] = useState({ pepy: false, pypi: false, codecov: false, ready: false });

  useEffect(() => {
    let cancelled = false;
    setVisible({ pepy: false, pypi: false, codecov: false, ready: false });

    (async () => {
      const urls = buildExternalBadgeUrls(repo);
      if (!urls) {
        if (!cancelled) setVisible({ pepy: false, pypi: false, codecov: false, ready: true });
        return;
      }

      const { pypiPackage } = urls;
      const covSlug = getCodecovRepoSlug(repo);
      const covParts = covSlug.split('/');
      const covOk = covParts.length === 2;
      const [co, cr] = covOk ? covParts : [null, null];
      const enc = encodeURIComponent;

      const tasks = [];
      if (pypiPackage) {
        tasks.push(
          fetchShieldOk(`https://img.shields.io/pypi/dm/${enc(pypiPackage)}.json`).then((ok) => ({
            key: 'pepy',
            ok,
          }))
        );
        tasks.push(
          fetchShieldOk(`https://img.shields.io/pypi/v/${enc(pypiPackage)}.json`).then((ok) => ({
            key: 'pypi',
            ok,
          }))
        );
      }
      if (covOk && co && cr) {
        tasks.push(
          fetchShieldOk(`https://img.shields.io/codecov/c/github/${enc(co)}/${enc(cr)}.json`).then((ok) => ({
            key: 'codecov',
            ok,
          }))
        );
      }

      if (tasks.length === 0) {
        if (!cancelled) setVisible({ pepy: false, pypi: false, codecov: false, ready: true });
        return;
      }

      const next = { pepy: false, pypi: false, codecov: false, ready: true };
      try {
        const results = await Promise.all(tasks);
        for (const r of results) {
          if (r.key === 'pepy') next.pepy = r.ok;
          if (r.key === 'pypi') next.pypi = r.ok;
          if (r.key === 'codecov') next.codecov = r.ok;
        }
      } catch {
      }
      if (!cancelled) setVisible(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [repo]);

  const urls = buildExternalBadgeUrls(repo);
  if (!urls || !visible.ready) return null;

  const { pypiPackage, pepy, pypiVersionSvg, codecovImg, codecovHref } = urls;
  const label = title || repo || 'Project';

  const showPepy = visible.pepy && pypiPackage && pepy;
  const showPypi = visible.pypi && pypiPackage && pypiVersionSvg;
  const showCodecov = visible.codecov && codecovImg && codecovHref;

  if (!showPepy && !showPypi && !showCodecov) return null;

  return (
    <div className={styles.badges}>
      {showPepy && (
        <a
          href={`https://pepy.tech/projects/${encodeURIComponent(pypiPackage)}`}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.badgeLink}
        >
          <img
            src={pepy}
            alt={`${label}: PyPI download count (Pepy)`}
            className={styles.badgeImg}
            loading="lazy"
            decoding="async"
          />
        </a>
      )}
      {showPypi && (
        <a
          href={`https://pypi.org/project/${encodeURIComponent(pypiPackage)}/`}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.badgeLink}
        >
          <img
            src={pypiVersionSvg}
            alt={`${label}: PyPI version`}
            className={styles.badgeImg}
            loading="lazy"
            decoding="async"
          />
        </a>
      )}
      {showCodecov && (
        <a href={codecovHref} target="_blank" rel="noreferrer noopener" className={styles.badgeLink}>
          <img
            src={codecovImg}
            alt={`${label}: Codecov coverage`}
            className={styles.badgeImg}
            loading="lazy"
            decoding="async"
          />
        </a>
      )}
    </div>
  );
};

const RESERVED_GITHUB_PATHS = new Set([
  'topics',
  'settings',
  'orgs',
  'features',
  'marketplace',
  'apps',
  'sponsors',
  'enterprise',
  'security',
  'readme',
  'collections',
  'explore',
  'issues',
  'pulls',
  'compare',
  'login',
  'signup',
  'pricing',
  'about',
]);

function parseGithubRepoFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/github\.com\/([\w.-]+)\/([\w.-]+)/i);
  if (!m) return null;
  const owner = m[1];
  if (RESERVED_GITHUB_PATHS.has(owner.toLowerCase())) return null;
  const repo = m[2].replace(/\.git$/i, '');
  if (!owner || !repo) return null;
  return `${owner}/${repo}`;
}

function normalizeUrlKey(url) {
  return String(url || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase();
}

function mergeOpenSourceProjects(contentfulRows, staticItems) {
  const byRepo = new Map();
  const byLink = new Map();
  for (const item of staticItems) {
    if (item.repo) byRepo.set(item.repo, item);
    if (item.link) byLink.set(normalizeUrlKey(item.link), item);
  }

  return contentfulRows.map((row) => {
    const repo = parseGithubRepoFromUrl(row.link);
    const isGithub = Boolean(repo);
    const base =
      (repo && byRepo.get(repo)) || byLink.get(normalizeUrlKey(row.link)) || null;

    if (base) {
      return {
        ...base,
        contentfulId: row.contentfulId,
        name: row.name,
        link: row.link,
        repo: repo || base.repo,
        isGithub: isGithub || base.isGithub,
        githubUrl: isGithub ? row.link : null,
        externalUrl: isGithub ? null : row.link,
        logoUrl: row.logoUrl || base.logoUrl,
      };
    }

    return {
      contentfulId: row.contentfulId,
      name: row.name,
      link: row.link,
      repo,
      isGithub,
      description: '',
      topics: [],
      logoUrl: row.logoUrl || null,
      githubUrl: isGithub ? row.link : null,
      externalUrl: isGithub ? null : row.link,
      docsUrl: '',
      slackUrl: null,
      badges: isGithub
        ? [
            { type: 'downloads', value: '—' },
            { type: 'pypi', value: '—' },
            { type: 'codecov', value: '—' },
          ]
        : [],
    };
  });
}

const DocsIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#111517" strokeWidth="1.5" fill="none"/>
    <line x1="7" y1="8" x2="17" y2="8" stroke="#111517" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="12" x2="17" y2="12" stroke="#111517" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="16" x2="13" y2="16" stroke="#111517" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SlackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#111517"/>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="#111517" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 3h6v6" stroke="#111517" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 14L21 3" stroke="#111517" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ImagePlaceholder = () => (
  <div className={styles.placeholder}>
    <svg viewBox="0 0 100 75" xmlns="http://www.w3.org/2000/svg" className={styles.placeholderSvg}>
      <rect x="1" y="1" width="98" height="73" fill="none" stroke="#b0bec5" strokeWidth="1.5"/>
      <line x1="1" y1="1" x2="99" y2="74" stroke="#b0bec5" strokeWidth="1.5"/>
      <line x1="99" y1="1" x2="1" y2="74" stroke="#b0bec5" strokeWidth="1.5"/>
    </svg>
  </div>
);

const ProjectLogo = ({ logoUrl, title }) => {
  const [loadFailed, setLoadFailed] = useState(false);

  if (!logoUrl || loadFailed) {
    return <ImagePlaceholder />;
  }

  return (
    <img
      src={logoUrl}
      alt={`${title} logo`}
      className={styles.logo}
      loading="lazy"
      decoding="async"
      onError={() => setLoadFailed(true)}
    />
  );
};

const STATIC_OPEN_SOURCE_ITEMS = openSourceProjectsData.items || [];

const OpenSourcePage = () => {
  usePageMeta(pagesSeo.openSource);
  const [projects, setProjects] = useState(STATIC_OPEN_SOURCE_ITEMS);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const rows = await fetchOpenSourceLibraries();
        if (cancelled) return;
        if (rows.length === 0) {
          setProjects(STATIC_OPEN_SOURCE_ITEMS);
          return;
        }
        setProjects(mergeOpenSourceProjects(rows, STATIC_OPEN_SOURCE_ITEMS));
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load open source libraries from Contentful', err);
        setLoadError(err?.message || 'Could not refresh list from Contentful.');
        setProjects(STATIC_OPEN_SOURCE_ITEMS);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.container}>
        <h1
          className={`${styles.title} page-primary-title`}
          style={{ ...typography.heading.large, color: colors.dark }}
        >
          OPEN SOURCE PROJECTS
        </h1>

        {loadError && (
          <p
            role="status"
            style={{
              ...typography.body.small.regular,
              color: colors.gray[700],
              marginTop: 0,
              marginBottom: 16,
            }}
          >
            {loadError} Showing cached project data.
          </p>
        )}

        <div className={styles.divider} />

        <div className={styles.grid}>
          {projects.map((project) => (
            <div key={project.contentfulId || project.name + project.link} className={styles.card}>
              <ProjectLogo logoUrl={project.logoUrl} title={project.name} />

              <h2 className={styles.projectName}>
                {project.githubUrl ? (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.projectTitle}
                  >
                    {project.name}
                  </a>
                ) : (
                  <span className={styles.projectTitle}>{project.name}</span>
                )}
              </h2>

              <p className={styles.description}>{project.description || '—'}</p>

              {project.topics && project.topics.length > 0 && (
                <div className={styles.tags}>
                  {project.topics.slice(0, 3).map((tag) => (
                    <a
                      key={tag}
                      href={`https://github.com/topics/${encodeURIComponent(tag)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.tag}
                    >
                      {tag}
                    </a>
                  ))}
                </div>
              )}

              {project.repo && (
                <ExternalProjectBadges repo={project.repo} title={project.name} />
              )}

              <div className={styles.links}>
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.iconLink}
                    aria-label="GitHub"
                  >
                    <GithubIcon color={colors.dark} size={24} />
                  </a>
                )}
                {project.externalUrl && (
                  <a
                    href={project.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.iconLink}
                    aria-label="Project link"
                  >
                    <ExternalLinkIcon />
                  </a>
                )}
                {project.docsUrl && (
                  <a
                    href={project.docsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.iconLink}
                    aria-label="Documentation"
                  >
                    <DocsIcon />
                  </a>
                )}
                {project.slackUrl && (
                  <a
                    href={project.slackUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.iconLink}
                    aria-label="Slack"
                  >
                    <SlackIcon />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OpenSourcePage;
